export const formatIndianCurrency = (value) => {
    const num = Math.round(value);
    const formatter = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    if (num >= 10000000) { // Crores
        return `₹${(num / 10000000).toFixed(2)} Cr`;
    }
    if (num >= 100000) { // Lakhs
        return `₹${(num / 100000).toFixed(2)} L`;
    }
    return formatter.format(num);
};

export const formatToLakhs = (value) => {
    const num = Math.round(value);
    if (num < 100000) {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(num);
    }
    return `₹${(num / 100000).toFixed(2)} L`;
};

/**
 * Formats a Date object into a "YYYY-MM-DD" string based on the user's local timezone.
 * @param {Date} date The date to format.
 * @returns {string} The formatted date string.
 */
export const formatDateToYMD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};


/**
 * Formats a date into a relative time string (e.g., "5m ago").
 * @param {Date | null} date The date to format.
 * @returns {string} A relative time string.
 */
export const formatRelativeTime = (date) => {
    if (!date) return 'never';
    const now = new Date();
    const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
    
    if (seconds < 5) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.round(hours / 24);
    return `${days}d ago`;
};

/**
 * Formats a date range into a display string like "26-Oct-23 - 25-Nov-23".
 * @param {string} startDateStr The start date in "YYYY-MM-DD" format.
 * @param {string} endDateStr The end date in "YYYY-MM-DD" format.
 * @returns {string} A formatted date range string.
 */
export const formatDisplayDateRange = (startDateStr, endDateStr) => {
    const formatDate = (date) => {
        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = date?.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' });
        const year = String(date.getUTCFullYear()).slice(-2);
        return `${day}-${month}-${year}`;
    };
    
    const startDateParts = startDateStr.split('-').map(Number);
    const endDateParts = endDateStr.split('-').map(Number);
    
    // Create UTC date to avoid local timezone issues
    const startDate = new Date(Date.UTC(startDateParts[0], startDateParts[1] - 1, startDateParts[2]));
    const endDate = new Date(Date.UTC(endDateParts[0], endDateParts[1] - 1, endDateParts[2]));

    const startFormatted = formatDate(startDate);
    const endFormatted = formatDate(endDate);

    return `${startFormatted} - ${endFormatted}`;
};

/**
 * Formats a date string "YYYY-MM-DD" into a short format like "25 Dec 2023".
 * @param {string} dateStr The date string to format.
 * @returns {string} A formatted short date string.
 */
export const formatShortDate = (dateStr) => {
    const dateParts = dateStr.split('-').map(Number);
    // Create UTC date to avoid local timezone issues
    const date = new Date(Date.UTC(dateParts[0], dateParts[1] - 1, dateParts[2]));
    return date?.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
    });
};